import React, { useRef, useEffect, useState, useMemo } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import { getFilterOptionsWithIntegrations } from "./filterOptions.jsx";

const FilterTypeSelector = ({
  isVisible,
  onSelect,
  onClose,
  buttonPosition = null,
  containerWidth = 800,
  customFields = {},
  integrations = {},
}) => {
  const dropdownRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Reset search when component becomes visible
  useEffect(() => {
    if (isVisible) {
      setSearchTerm("");
      setSelectedIndex(0);
    }
  }, [isVisible]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    const optionsWithIntegrations = getFilterOptionsWithIntegrations(
      integrations,
      customFields,
    );

    if (!searchTerm.trim()) {
      return optionsWithIntegrations;
    }

    const searchLower = searchTerm.toLowerCase();
    return optionsWithIntegrations.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.description.toLowerCase().includes(searchLower) ||
        option.type.toLowerCase().includes(searchLower),
    );
  }, [searchTerm, customFields, integrations]);

  // Reset selected index when filtered options change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredOptions]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isVisible || filteredOptions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredOptions.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(
          (prev) =>
            (prev - 1 + filteredOptions.length) % filteredOptions.length,
        );
        break;
      case "Enter":
        e.preventDefault();
        if (filteredOptions[selectedIndex]) {
          onSelect(filteredOptions[selectedIndex].type);
        }
        break;
      case "Escape":
        e.preventDefault();
        onClose();
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  // Calculate positioning based on button position with overflow detection
  const getPositionStyles = () => {
    if (buttonPosition) {
      // Use minimum width for FilterTypeSelector
      const minSelectorWidth = 250; // Minimum width of the selector
      const gap = 8;
      const inlineLeft = buttonPosition.x + buttonPosition.width + gap;

      // Check if FilterTypeSelector would overflow the container
      const wouldOverflow = inlineLeft + minSelectorWidth > containerWidth;

      if (wouldOverflow) {
        // Position on new row at the rightmost position
        const rightAlignedLeft = containerWidth - minSelectorWidth;
        return {
          position: "absolute",
          left: `${rightAlignedLeft}px`,
          top: `${buttonPosition.y + buttonPosition.height + 8}px`, // 8px gap below button
          zIndex: 50,
        };
      } else {
        // Position inline after the button, with flexible offset based on button position
        const topOffset = buttonPosition.y > 0 ? buttonPosition.y - 12 : -24;
        return {
          position: "absolute",
          left: `${inlineLeft}px`,
          top: `${topOffset}px`,
          zIndex: 50,
        };
      }
    }

    // Fallback to original positioning logic
    return {
      position: "absolute",
      top: "0px",
      left: "0px",
      zIndex: 50,
    };
  };

  return (
    <div ref={dropdownRef} className="relative">
      <div
        className="bg-white border border-gray-300 rounded-lg shadow-lg"
        style={{
          ...getPositionStyles(),
          minWidth: "250px",
        }}
      >
        <div className="p-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                // Let the global handler handle navigation keys
                if (
                  ["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(e.key)
                ) {
                  e.preventDefault();
                  handleKeyDown(e);
                }
              }}
              placeholder="Search filters..."
              className="flex-1 text-sm px-3 py-1 border-0.5 border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-pink focus:border-transparent placeholder-gray-400"
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-[80vh] overflow-y-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <button
                key={option.type}
                onClick={() => onSelect(option.type)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`w-full flex items-center gap-2 p-2 text-left border-b border-gray-100 last:border-b-0 ${
                  index === selectedIndex
                    ? "bg-gray-100 border-gray-300"
                    : "hover:bg-gray-50"
                }`}
                type="button"
              >
                <span className="text-lg">{option.icon}</span>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">
                    {option.label}
                  </div>
                </div>
              </button>
            ))
          ) : (
            <div className="p-3 text-center text-gray-500 text-sm">
              No filter options found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterTypeSelector;
